import React, { useState } from "react";
import { Layers, Plus, Pencil } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import StatusBadge from "../components/StatusBadge";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import { getEmailMarketingPrograms, createProgram, updateProgram } from "../../../lib/emailMarketingApi";

// EMAILS-IMPL-007: Program authoring. Program has NO governed
// entity-lifecycle vocabulary (CLS-001: "Programs do not expire"; no
// Program-level spec defines a Status Lifecycle section -- confirmed
// directly during EMAILS-IMPL-006's own governance re-read). `status` is
// therefore a plain optional free-text field here, never a fixed dropdown
// -- presenting a fake enum would misrepresent governance that does not
// exist.
function emptyForm() {
  return { name: "", status: "" };
}

export default function ProgramsPanel() {
  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem } =
    useEmailCatalogEntityList({
      list: getEmailMarketingPrograms,
      create: createProgram,
      update: updateProgram,
      getId: (row) => row.program_id,
    });

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [editingProgram, setEditingProgram] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());

  const openCreate = () => {
    setCreateForm(emptyForm());
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({ name: createForm.name.trim(), status: createForm.status.trim() || null });
      setCreateOpen(false);
    } catch {
      // mutationError already surfaced by the hook; keep the dialog open.
    }
  };

  const openEdit = (program) => {
    setEditingProgram(program);
    setEditForm({ name: program.name, status: program.status || "" });
    clearMutationError();
  };

  const submitEdit = async () => {
    // Only include fields that actually changed -- `undefined` keys are
    // dropped by JSON.stringify, matching the backend's PATCH semantics
    // (Optional[str] = None means "leave untouched", not "clear it"; there
    // is no way to explicitly null out an already-set field via this API,
    // by design -- see EMAILS-IMPL-007's completion report).
    try {
      await updateItem(editingProgram.program_id, {
        name: editForm.name.trim() !== editingProgram.name ? editForm.name.trim() : undefined,
        status: editForm.status.trim() !== (editingProgram.status || "") ? editForm.status.trim() || null : undefined,
      });
      setEditingProgram(null);
    } catch {
      // dialog stays open; error shown inline
    }
  };

  if (loading) return <CatalogLoadingCard testId="programs-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="programs-error" />;

  return (
    <div className="space-y-4" data-testid="programs-panel">
      <CatalogTable
        testId="programs-table"
        title="Programs"
        icon={Layers}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            data-testid="new-program-button"
          >
            <Plus className="h-4 w-4" /> New Program
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.program_id}
        onRowClick={(row) => setExpandedId(expandedId === row.program_id ? null : row.program_id)}
        emptyTitle="No Programs yet"
        emptySubtitle='Create one with "New Program" to get started.'
        columns={[
          {
            key: "name",
            label: "Program",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row.program_id}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`program-status-${row.program_id}`} /> },
          { key: "version", label: "Version", render: (row) => <span className="font-mono text-sm text-slate-300">{row.version || "—"}</span> },
        ]}
      />

      {items
        .filter((row) => row.program_id === expandedId)
        .map((row) => (
          <div key={row.program_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`program-detail-${row.program_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <Detail label="Program ID" value={row.program_id} mono />
                <Detail label="Status" value={row.status || "—"} />
                <Detail label="Version" value={row.version || "—"} mono />
                <Detail label="Created" value={row.created_at ? new Date(row.created_at).toLocaleString() : "—"} />
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5 border-white/10 text-slate-200"
                onClick={() => openEdit(row)}
                data-testid={`edit-program-${row.program_id}`}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            </div>
          </div>
        ))}

      <EntityFormDialog
        testId="create-program-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Program"
        description="Programs are permanent, top-level business processes (e.g. Cold Acquisition)."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Program"
      >
        <div className="space-y-2">
          <Label htmlFor="program-name">Name</Label>
          <Input
            id="program-name"
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Cold Acquisition"
            required
            data-testid="program-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="program-status">Status (optional, free text)</Label>
          <Input
            id="program-status"
            value={createForm.status}
            onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value }))}
            placeholder="Program has no fixed lifecycle"
            data-testid="program-status-input"
          />
        </div>
      </EntityFormDialog>

      {editingProgram && (
        <EntityFormDialog
          testId="edit-program-dialog"
          open={!!editingProgram}
          onOpenChange={(open) => !open && setEditingProgram(null)}
          title={`Edit ${editingProgram.program_id}`}
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor="program-edit-name">Name</Label>
            <Input
              id="program-edit-name"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid="program-edit-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="program-edit-status">Status (optional, free text)</Label>
            <Input
              id="program-edit-status"
              value={editForm.status}
              onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
              data-testid="program-edit-status-input"
            />
          </div>
        </EntityFormDialog>
      )}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
